import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { listOfStates } from "@onsip/common/interfaces/state";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";
import phoneNumberValidator from "@onsip/common/libraries/validators/phone-number-validators";
import {
  Organization,
  OrganizationService
} from "@onsip/common/services/api/resources/organization/organization.service";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { BehaviorSubject, Subscription } from "rxjs";

@Component({
  selector: "onsip-contact-edit",
  templateUrl: "./contact-edit.component.html",
  styleUrl: "./contact-edit.component.scss",
  standalone: false
})
export class ContactEditComponent implements OnInit, OnDestroy {
  @Input() organization!: Organization;
  @Output() editPanel = new EventEmitter<void>();
  organizationService = inject(OrganizationService);
  translate = inject(TranslateService);
  snackbar = inject(SnackbarService);

  form!: FormGroup;
  listOfStates = listOfStates;
  loadingStateComplete$ = new BehaviorSubject(true);
  autoCompleteState = new FormControl("");
  private unsubcriber = new Subscription();

  ngOnInit(): void {
    this.initForm();
    this.fillForm();
  }

  ngOnDestroy(): void {
    this.unsubcriber.unsubscribe();
  }

  initForm(): void {
    this.form = new FormGroup({
      organization: new FormControl("", {
        validators: [Validators.maxLength(60), Validators.pattern(NAME_VALIDATOR_REGEX)]
      }),
      address: new FormControl(),
      city: new FormControl(),
      state: new FormControl(),
      zipcode: new FormControl(),

      name: new FormControl("", {
        validators: [Validators.maxLength(60), Validators.pattern(NAME_VALIDATOR_REGEX)]
      }),
      email: new FormControl("", { validators: Validators.email }),
      phone: new FormControl("", { validators: phoneNumberValidator() })
    });
  }

  fillForm() {
    const contact = this.organization.contact;
    this.form.setValue({
      organization: contact.organization,
      address: contact.address,
      city: contact.city,
      state: contact.state,
      zipcode: contact.zipcode,
      name: contact.name,
      email: contact.email,
      phone: contact.phone
    });
    this.autoFillState();
  }

  private autoFillState() {
    this.unsubcriber.add(
      this.autoCompleteState.valueChanges.subscribe(res => {
        if (res) {
          this.form.get("state")?.setValue(res);
        }
      })
    );
  }

  getNameError(value: "organization" | "name"): string {
    const control = this.form.controls[value];
    value =
      value === "organization"
        ? this.translate.instant("ONSIP_I18N.COMPANY")
        : this.translate.instant("ONSIP_I18N.CONTACT_NAME");
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : control.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_LONGER_THAN_60_CHARACTERS", { value })
      : control.hasError("pattern")
      ? this.translate.instant(
          "ONSIP_I18N._VALUE__CAN_ONLY_CONTAIN_LETTERS_SPACES_QUOTES_OR_DASHES",
          { value }
        )
      : "";
  }

  getEmailError(): string {
    const control = this.form.controls.email;
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N.EMAIL_IS_REQUIRED")
      : control.hasError("email")
      ? this.translate.instant("ONSIP_I18N.INVALID_EMAIL")
      : "";
  }

  getPhoneError(): string {
    const control = this.form.controls.phone;
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N.PHONE_NUMBER_IS_REQUIRED")
      : control.hasError("validPhoneNumber")
      ? this.translate.instant("ONSIP_I18N.MUST_BE_A_VALID_10DIGIT_PHONE_NUMBER")
      : "";
  }

  save() {
    const data = this.form.value;
    this.loadingStateComplete$.next(false);
    this.organizationService.organizationEditContact(data).then(response => {
      if (response.status === "error") {
        this.snackbar.openSnackBar(response.data.message, "error");
        this.organizationService.clearErrors();
      } else {
        this.snackbar.openSnackBar(
          this.translate.instant("ONSIP_I18N.YOUR_CONTACT_INFO_HAS_BEEN_SAVED"),
          "success"
        );
        this.togglePanel();
      }
      this.loadingStateComplete$.next(true);
    });
  }

  private togglePanel() {
    this.editPanel.emit();
  }

  cancel() {
    this.togglePanel();
  }
}
